import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import type { WhatsappTemplate } from '../types/whatsapp';

interface TestSendBlockProps {
  template: WhatsappTemplate;
  variableNumbers: number[];
  mediaFile: File | null;
  buttonSuffix: string;
  couponCode: string;
  disabled: boolean;
  values: Record<string, string>;
  onValueChange: (number: number, value: string) => void;
}

export default function TestSendBlock({
  template,
  variableNumbers,
  mediaFile,
  buttonSuffix,
  couponCode,
  disabled,
  values,
  onValueChange,
}: TestSendBlockProps) {
  const [phone, setPhone] = useState('');

  const sendTest = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('template_meta_id', template.meta_id);
      form.append('recipient_phone', phone.trim());
      if (buttonSuffix) form.append('button_suffix', buttonSuffix);
      if (couponCode) form.append('coupon_code', couponCode);
      if (mediaFile) form.append('header_media', mediaFile);
      variableNumbers.forEach((number) =>
        form.append('variable_values', values[String(number)] ?? ''),
      );

      return axios.post(endpoints.whatsapp.sendTest, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => toast.success('Test message sent'),
  });

  const missingValue = variableNumbers.some(
    (number) => !(values[String(number)] ?? '').trim(),
  );
  const canSend = phone.trim() !== '' && !missingValue && !disabled;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h3 className="text-sm font-medium text-gray-900">
        Send yourself a test
      </h3>
      <p className="mt-1 text-xs text-gray-500">
        This sends one real WhatsApp message. It is delivered and billed like
        any other.
      </p>

      <div className="mt-4 space-y-3">
        {variableNumbers.map((number) => (
          <div key={number}>
            <label
              htmlFor={`sample-${number}`}
              className="mb-1 block text-sm text-gray-700"
            >
              Sample value for {`{{${number}}}`}
            </label>
            <input
              id={`sample-${number}`}
              type="text"
              value={values[String(number)] ?? ''}
              onChange={(event) => onValueChange(number, event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ))}

        <div>
          <label
            htmlFor="test-phone"
            className="mb-1 block text-sm text-gray-700"
          >
            Test number
          </label>
          <div className="flex gap-2">
            <input
              id="test-phone"
              type="tel"
              value={phone}
              placeholder="+27821234567"
              onChange={(event) => setPhone(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => sendTest.mutate()}
              disabled={!canSend || sendTest.isPending}
              className="shrink-0 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sendTest.isPending ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
